<template>
	<div>
		<div class="flex items-end justify-between mb-2">
			<div>
				<p class="font-semibold text-lg">
					{{ title }}
				</p>
				<div class="h-0 w-16 border-[0.1875rem] rounded-full" :class="borderColorClass"></div>
			</div>
			<button
				v-if="!showLoading && authorDataList.length > maxItemsToShow"
				type="button"
				class="btn btn-sm btn-ghost"
				@click="$emit('selectGroup')"
			>
				<span class="flex items-center gap-1 normal-case">
					{{ $t("Show all") }}
				</span>
			</button>
		</div>
		<div class="border rounded-lg p-4" :class="borderColorClass">
			<div v-if="showLoading" class="flex items-center justify-center py-12">
				<span class="loading loading-spinner loading-md"></span>
			</div>
			<slot name="emptyState" v-else-if="authorDataList.length === 0">
				<p class="text-sm py-4">
					{{ $t('No authors found') }}
				</p>
			</slot>
			<div v-else class="flex flex-wrap gap-x-8">
				<button
					v-for="authorData in authorDataListToShow"
					:key="authorData.author.id"
					type="button"
					class="group inline-flex items-center rounded cursor-pointer border-none bg-white hover:bg-base-100 py-2 w-full justify-between min-h-fit"
					:class="maxCardWidth"
					@click="$emit('selectAuthor', authorData.author, authorData.section)"
				>
					<div class="flex flex-1 gap-4 items-center px-2 min-w-0">
						<UserAvatar :user="authorData.author" :usePublicProfile="authorData.section !== 'colleagues'" class="group-hover:bg-base-200" :class="avatarSize" />
						<div class="flex-1 min-w-0">
							<div class="text-start leading-tight">
								<p class="font-semibold line-clamp-2 break-words">{{ authorData.section === 'colleagues'? authorData.author.name : authorData.author.publicName ?? authorData.author.name }}</p>
							</div>
							<div v-if="showLessonPlanCounter" class="text-xs text-base-content-light lowercase block truncate break-words hyphens-auto mt-0.5 text-left">
								{{ $t('{count} lesson plans', { count: authorData.totalPlanCount }) }}
							</div>
						</div>
					</div>
					<div class="flex items-center pr-2">
						<ChevronRightIcon class="w-5 h-5 text-base-content/40" />
					</div>
				</button>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import UserAvatar from "@/components/UserAvatar.vue";
import { ChevronRightIcon } from "@heroicons/vue/24/solid";
import { AuthorData } from "@/stores/DiscoverCollections.store";

export default {
	name: 'LessonPlanDiscoverSection',
	emits: ['selectAuthor', 'selectGroup'],

	props: {
		title: {
			type: String,
			required: true
		},
		showLoading: {
			type: Boolean,
			default: false
		},
		authorDataList: {
			type: Array as () => AuthorData[],
			required: true
		},
		maxItemsToShow: {
			type: Number,
			default: 6
		},
		showLessonPlanCounter: {
			type: Boolean,
			default: true
		},
		borderColorClass: {
			type: String,
			required: false,
			default: ''
		},
		cardSize: {
			type: String as () => 'sm' | 'md',
			default: 'md'
		},
	},

	components: {
		UserAvatar,
		ChevronRightIcon,
	},

	computed: {
		maxCardWidth(): string {
			switch (this.cardSize) {
				case 'sm':
					return 'max-w-48';
				case 'md':
					return 'max-w-80';
				default:
					return 'max-w-80';
			}
		},
		avatarSize(): string {
			switch (this.cardSize) {
				case 'sm':
					return 'h-10 w-10';
				case 'md':
					return 'h-20 w-20';
				default:
					return 'h-20 w-20';
			}
		},
		authorDataListToShow(): AuthorData[] {
			if(this.maxItemsToShow) {
				return this.authorDataList.slice(0, this.maxItemsToShow);
			}
			return this.authorDataList;
		}
	},
};
</script>
